import { useState, useEffect } from 'react'
import type { Organisation, OrganisationInput } from '@esf/shared'

interface OrganisationFormProps {
  organisation?: Organisation | null
  onSubmit: (data: OrganisationInput) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export default function OrganisationForm({ organisation, onSubmit, onCancel, isLoading }: OrganisationFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [autoSlug, setAutoSlug] = useState(true)

  useEffect(() => {
    if (organisation) {
      setFormData({
        name: organisation.name,
        slug: organisation.slug,
        contactEmail: organisation.contactEmail || '',
        contactPhone: organisation.contactPhone || '',
        address: organisation.address || '',
      })
      setAutoSlug(false)
    }
  }, [organisation])

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 30)
  }

  const handleNameChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: autoSlug && !organisation ? generateSlug(value) : prev.slug,
    }))
    if (errors.name) {
      setErrors(prev => {
        const next = { ...prev }
        delete next.name
        return next
      })
    }
  }

  const handleChange = (field: string, value: string) => {
    if (field === 'slug') {
      setAutoSlug(false)
    }
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Organisation name is required'
    }

    if (!formData.slug?.trim()) {
      newErrors.slug = 'Slug is required'
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens'
    }

    if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const cleanData: OrganisationInput = {
      name: formData.name,
      slug: formData.slug,
      contactEmail: formData.contactEmail || undefined,
      contactPhone: formData.contactPhone || undefined,
      address: formData.address || undefined,
    }

    await onSubmit(cleanData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Organisation Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={e => handleNameChange(e.target.value)}
          className={`filter-input w-full ${errors.name ? 'border-red-500' : ''}`}
          placeholder="Acme Corporation"
        />
        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
      </div>

      {/* Slug */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Slug <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={formData.slug}
            onChange={e => handleChange('slug', e.target.value.toLowerCase())}
            className={`filter-input flex-1 font-mono ${errors.slug ? 'border-red-500' : ''}`}
            placeholder="acme-corp"
            disabled={!!organisation}
          />
          {!organisation && autoSlug && (
            <span className="text-xs text-gray-500">Auto-generated</span>
          )}
        </div>
        {errors.slug && <p className="text-red-500 text-xs mt-1">{errors.slug}</p>}
        {organisation && <p className="text-xs text-gray-500 mt-1">Slug cannot be changed after creation</p>}
      </div>

      {/* Contact Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
        <input
          type="email"
          value={formData.contactEmail || ''}
          onChange={e => handleChange('contactEmail', e.target.value)}
          className={`filter-input w-full ${errors.contactEmail ? 'border-red-500' : ''}`}
          placeholder="contact@organisation.com"
        />
        {errors.contactEmail && <p className="text-red-500 text-xs mt-1">{errors.contactEmail}</p>}
      </div>

      {/* Contact Phone */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
        <input
          type="tel"
          value={formData.contactPhone || ''}
          onChange={e => handleChange('contactPhone', e.target.value)}
          className="filter-input w-full"
          placeholder="+44 20 7946 0000"
        />
      </div>

      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
        <textarea
          value={formData.address || ''}
          onChange={e => handleChange('address', e.target.value)}
          className="filter-input w-full"
          rows={2}
          placeholder="123 Business Street, London, UK"
        />
      </div>

      {/* Organisation Status (only when editing) */}
      {organisation && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Organisation Status</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                {organisation.enabled !== false
                  ? 'Organisation is active and users can access the platform'
                  : 'Organisation is disabled and users cannot sign in'}
              </p>
            </div>
            <span className={`badge ${organisation.enabled !== false ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-600'}`}>
              {organisation.enabled !== false ? 'Active' : 'Disabled'}
            </span>
          </div>
          {organisation.isEsfOrg && (
            <div className="mt-3 flex items-center gap-2 text-esf-coral">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clipRule="evenodd" />
              </svg>
              <span className="text-xs font-medium">Platform Organisation</span>
            </div>
          )}
          {organisation.createdAt && (
            <p className="text-xs text-gray-500 mt-2">
              Created: {new Date(organisation.createdAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Saving...
            </>
          ) : organisation ? 'Update Organisation' : 'Create Organisation'}
        </button>
      </div>
    </form>
  )
}
